import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { baseqlMetaData } from '@adornis/baseql/metadata/metaDataStore.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { BuddyMailTemplate, MailTemplateType } from '@adornis/bh-matching-mails/entities/mail-templates.js';
import { mmr } from '@adornis/bh-matching-mails/mail-registry.js';
import { BuddyUser, BuddyUserMentee, BuddyUserMentor } from '@adornis/bh-matching-user/entities/buddy-user.js';
import { MatchingPermission } from '@adornis/bh-matching-user/permissions.js';
import { mailer } from '@adornis/buchungsheld-db/mails/communication.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import type { InsertOneResult } from 'mongodb';
import { getUserClasses } from '../user-class.js';

/**
 * @entity MatchComment
 */
@Entity()
export class MatchComment extends AdornisEntity {
  static override _class = 'MatchComment';

  @Field(type => ID, { default: v => v ?? A.getGloballyUniqueID() })
  _id!: string;

  @Field(type => String)
  text: Maybe<string>;

  @Field(type => Date)
  createdAt: Maybe<Date>;

  @Field(type => Boolean)
  isAlert: Maybe<boolean>;
}

/**
 * @entity Match
 */
@Entity()
export class Match extends MongoEntity {
  static override _class = 'Match';
  static override _collectionName = 'match';

  constructor(doc: PartialEntityData<Match>) {
    super(doc);
  }

  @Field(type => ID)
  mentorID: Maybe<string>;

  @Field(type => BuddyUserMentor, {
    resolve: function (this: Match) {
      return async () => {
        if (this.mentor?._id && baseqlMetaData.entities.validSubclasses(BuddyUserMentor).includes(this.mentor?._class))
          return this.mentor;
        const mentor = this.mentorID ? await loadMongoData(BuddyUserMentor, this.mentorID) : null;
        return mentor;
      };
    },
  })
  mentor: Maybe<BuddyUserMentor>;

  @Field(type => ID)
  menteeID: Maybe<string>;

  @Field(type => BuddyUserMentee, {
    resolve: function (this: Match) {
      return async () => {
        if (this.mentee?._id && baseqlMetaData.entities.validSubclasses(BuddyUserMentee).includes(this.mentee?._class))
          return this.mentee;
        const mentee = this.menteeID ? await loadMongoData(BuddyUserMentee, this.menteeID) : null;
        return mentee;
      };
    },
  })
  mentee: Maybe<BuddyUserMentee>;

  @validate(nonOptional())
  @Field(type => ID)
  timeframeID: Maybe<string>;

  @Field(type => [MatchComment])
  comments: Maybe<MatchComment[]>;

  @Field(type => Date)
  createdAt: Maybe<Date>;

  static override async beforeCreate(match: Match) {
    await super.beforeCreate(match);

    match.createdAt = new Date();
  }

  static override async afterCreate(match: Match, result: InsertOneResult) {
    await super.afterCreate(match, result);

    void this._sendMatchCreationMails(match);
  }

  static async _sendMatchCreationMails(match: Match) {
    const mentorTemplate = await BuddyMailTemplate.getMailTemplateByType(MailTemplateType.MATCHING_MENTOR)(
      selectionSet(() => BuddyMailTemplate, 1),
    );
    const menteeTemplate = await BuddyMailTemplate.getMailTemplateByType(MailTemplateType.MATCHING_MENTEE)(
      selectionSet(() => BuddyMailTemplate, 1),
    );

    if (!mentorTemplate)
      return logger.error(
        {
          mailTemplateType1: MailTemplateType.MATCHING_MENTEE,
          mailTemplateType2: MailTemplateType.MATCHING_MENTOR,
          mentorTemplate: !!mentorTemplate,
          menteeTemplate: !!menteeTemplate,
        },
        'Couldnt find buddy mail template',
      );

    const userClasses = getUserClasses();
    if (!match.mentorID || !match.menteeID)
      throw new Error('match IDs missing, this state should not be allowed to exist at this point');
    const mentor = await getByID(userClasses.mentor, match.mentorID)(selectionSet(() => userClasses.mentor, 2));
    const mentee = await getByID(userClasses.mentee, match.menteeID)(selectionSet(() => userClasses.mentee, 2));

    if (!mentor || !mentee)
      return logger.error(
        { mentor: !!mentor, mentee: !!mentee, matchID: match._id },
        'Couldnt find mentor or mentee for match mail',
      );

    try {
      const _mailer = await mailer;
      await mmr
        .matchingMentee(match._id, MailTemplateType.MATCHING_MENTEE)
        .send({ mailer: _mailer, recipients: [new Recipient(mentee.email, undefined, undefined)] });
      await mmr
        .matchingMentor(match._id, MailTemplateType.MATCHING_MENTOR)
        .send({ mailer: _mailer, recipients: [new Recipient(mentor.email, undefined, undefined)] });
    } catch (err) {
      logger.error({ err }, 'Couldnt send match mail to user');
    }
  }
}

export const addCommentOnMatch = registerMutation({
  operationName: 'addCommentOnMatch',
  resolve: (matchID: string, comment: string) => async () => {
    const currUser = await CurrentUserInfo.getMyself<BuddyUser>()({ roles: AdornisRoleToContextsWrapper.allFields });
    if (!context.serverContext && !currUser?.hasPermission(MatchingPermission.CREATE_EDIT_DELETE_USERS))
      throw new Error('No permission to edit notes on users');

    const updateResult = await (
      await getRawCollection(Match._collectionName)
    ).updateOne(
      { _id: matchID },
      { $push: { comments: new MatchComment({ text: comment, createdAt: new Date() }).toJSON() } },
    );
    if (updateResult.modifiedCount === 1) return true;
    throw new Error(`Couldnt add comment on match (matchID: ${matchID}; ${JSON.stringify(updateResult)})`);
  },
  type: () => Boolean,
  params: [
    { name: 'matchID', type: () => String },
    { name: 'comment', type: () => String },
  ],
  options: { allowHTTP: true },
});
